import React, { useState } from 'react'
import Calendar from 'react-calendar'
import {
  format,
  startOfDay,
  addMonths,
  endOfDay,
  isSameDay,
  isAfter,
  getUnixTime
} from 'date-fns'
import { append, without, includes, uniq, sortBy, map, prop, identity, times, isEmpty, dropWhile } from 'ramda'
import { parseDate } from 'helpers/date_input_helper'
import './style.css'

const SAME_TIMES = 'same_times'
const DIFFERENT_TIMES = 'different_times'

const slotFromDay = (day, timeSlots) => {
  return timeSlots.find(slot => {
    let slotDay = startOfDay(parseDate(slot.start_at))
    return isSameDay(slotDay, day)
  })
}

const timeSlotStartDates = timeSlots => {
  return sortBy(
    getUnixTime,
    uniq(map(item => startOfDay(parseDate(item)), map(prop('start_at'), timeSlots)))
  )
}

const initSameTimeSchedulingOptionState = timeSlots => {
  if (includes(timeSlots.length, [1, 0])) {
    return SAME_TIMES
  } else {
    let startTimes = uniq(timeSlots.map(({ start_at }) => format(parseDate(start_at), 'h:mm aaa')))
    let endTimes = uniq(timeSlots.map(({ end_at }) => format(parseDate(end_at), 'h:mm aaa')))

    return startTimes.length === 1 && endTimes.length === 1 ? SAME_TIMES : DIFFERENT_TIMES
  }
}

const TimeSlotScheduler = ({ formObjectName, initialTimeSlots, readonly = false, initialEndTime = '5:00 pm' }) => {
  const [sameTimeSchedulingOption, setSameTimeSchedulingOption] = useState(
    initSameTimeSchedulingOptionState(initialTimeSlots)
  )
  const [days, setDays] = useState(timeSlotStartDates(initialTimeSlots))
  const [removedDays, setRemovedDays] = useState([])

  const calendarOnClickDay = (value, _event) => {
    if (readonly) return

    let startOfToday = startOfDay(new Date())
    let now = startOfDay(value)

    if (isAfter(now, startOfToday) || isSameDay(now, startOfToday)) {
      setRemovedDays(without([value], removedDays))
      setDays(sortBy(getUnixTime, uniq(append(value, days))))
    } else {
      console.log(`Ignoring add day: ${value} is in the past.`)
    }
  }

  const removeDay = day => {
    if (readonly) return

    console.log(`Removing day: ${day}`)
    setRemovedDays(uniq(append(day, removedDays)))
    setDays(without([day], days))
  }

  const tileClassName = ({ date }) => {
    if (includes(date, days)) {
      return 'dateSelected'
    } else {
      return ''
    }
  }

  let schedulerPanel
  if (sameTimeSchedulingOption === SAME_TIMES) {
    schedulerPanel = (
      <SameTimeScheduler
        formObjectName={formObjectName}
        days={days}
        setDays={setDays}
        removedDays={removedDays}
        initialTimeSlots={initialTimeSlots}
        removeDay={removeDay}
        readonly={readonly}
        initialEndTime={initialEndTime}
      />
    )
  } else {
    schedulerPanel = (
      <DifferentTimeScheduler
        formObjectName={formObjectName}
        days={days}
        setDays={setDays}
        removedDays={removedDays}
        initialTimeSlots={initialTimeSlots}
        removeDay={removeDay}
        readonly={readonly}
        initialEndTime={initialEndTime}
      />
    )
  }

  return (
    <div className='time-slot-scheduler col-10'>
      <Calendar
        className='w-100'
        minDate={startOfDay(new Date())}
        maxDate={endOfDay(addMonths(new Date(), 3))}
        prev2Label={null}
        next2Label={null}
        showNeighboringMonth
        showDoubleView={false}
        onClickDay={calendarOnClickDay}
        tileClassName={tileClassName}
        activeStartDate={null}
        calendarType='US'
        value={null}
      />

      {!readonly && (
        <div className='row'>
          <div className='m-3'>
            <div className='form-check'>
              <input
                className='form-check-input'
                type='radio'
                value={SAME_TIMES}
                id={SAME_TIMES}
                checked={sameTimeSchedulingOption === SAME_TIMES}
                onChange={event => setSameTimeSchedulingOption(event.target.value)}
              />
              <label className='form-check-label' htmlFor={SAME_TIMES}>
                Same time for each day
              </label>
            </div>
            <div className='form-check'>
              <input
                className='form-check-input'
                type='radio'
                value={DIFFERENT_TIMES}
                id={DIFFERENT_TIMES}
                checked={sameTimeSchedulingOption === DIFFERENT_TIMES}
                onChange={event => setSameTimeSchedulingOption(event.target.value)}
              />
              <label className='form-check-label' htmlFor={DIFFERENT_TIMES}>
                Different times for each day
              </label>
            </div>
          </div>
        </div>
      )}

      {schedulerPanel}
      <DeletedSlots
        formObjectName={formObjectName}
        initialTimeSlots={initialTimeSlots}
        removedDays={removedDays}
        baseIndex={days.length}
      />
    </div>
  )
}

const generateTimeSlices = () => {
  let startOfDayDate = startOfDay(new Date())
  return map(
    index => {
      const minutes = index * 15
      const date = new Date(startOfDayDate.getTime() + minutes * 60000)
      return format(date, 'h:mm aaa')
    },
    times(identity, 24 * 4)
  )
}

const TimeSelectOptions = ({ startingAt }) => {
  let timeSlices = generateTimeSlices()

  if (startingAt) timeSlices = dropWhile(time => time !== startingAt, timeSlices).slice(1)

  return timeSlices.map(time => (
    <option key={time} value={time}>
      {time}
    </option>
  ))
}

const EmptySchedule = () => {
  return <strong>Click on a date in the calendar to add it to the schedule</strong>
}

const DeletedSlots = ({ formObjectName, initialTimeSlots, removedDays, baseIndex = 0 }) => {
  const removedSlots = removedDays.map(day => slotFromDay(day, initialTimeSlots))

  return removedSlots.map(
    (slot, index) =>
      slot && (
        <div key={slot.start_at}>
          <input
            name={`${formObjectName}[time_slots_attributes][${index + baseIndex}][id]`}
            className='form-control hidden'
            type='hidden'
            value={slot.id}
          />
          <input
            name={`${formObjectName}[time_slots_attributes][${index + baseIndex}][_destroy]`}
            className='form-control hidden'
            type='hidden'
            value
          />
        </div>
      )
  )
}

const SameTimeScheduler = ({ formObjectName, days, initialTimeSlots, removeDay, readonly, initialEndTime }) => {
  const [state, setState] = useState(() => {
    const firstSlot = initialTimeSlots[0]
    if (firstSlot) {
      return {
        startTime: format(parseDate(firstSlot.start_at), 'h:mm aaa'),
        endTime: format(parseDate(firstSlot.end_at), 'h:mm aaa')
      }
    } else {
      return { startTime: '9:00 am', endTime: initialEndTime }
    }
  })

  const startAtChanged = event => {
    let eventValue = event.target.value
    return setState(state => ({ ...state, startTime: eventValue }))
  }

  const endAtChanged = event => {
    let eventValue = event.target.value
    return setState(state => ({ ...state, endTime: eventValue }))
  }

  return (
    <div className='row'>
      <div className='ms-3 list-group col-md-5'>
        <label>DATE(S) SELECTED</label>
        {isEmpty(days) ? (
          <div className='list-group-item list-group-item-action'>
            <EmptySchedule />
          </div>
        ) : (
          days
            .map(day => {
              return [day, slotFromDay(day, initialTimeSlots)]
            })
            .map(([day, slot], index) => (
              <div key={day.toString()} className='list-group-item list-group-item-action'>
                <span>{format(day, 'EEEE MMMM d, yyyy')}</span>

                {slot && slot.id && (
                  <input
                    name={`${formObjectName}[time_slots_attributes][${index}][id]`}
                    className='form-control hidden'
                    type='hidden'
                    value={slot.id}
                  />
                )}
                {!readonly && (
                  <a
                    className='text-danger float-end cursor-pointer'
                    id={`remove-${index}`}
                    onClick={() => {
                      removeDay(day)
                    }}
                  >
                    Remove
                  </a>
                )}
                <input
                  name={`${formObjectName}[time_slots_attributes][${index}][start_at]`}
                  className='form-control hidden'
                  type='hidden'
                  value={`${format(day, 'MMMM d, yyyy')} at ${state.startTime}`}
                />
                <input
                  name={`${formObjectName}[time_slots_attributes][${index}][end_time]`}
                  className='form-control hidden'
                  type='hidden'
                  value={state.endTime}
                />
              </div>
            ))
        )}
      </div>
      <div className='col-md-5'>
        <div className='form-group mb-3'>
          <label htmlFor='start_at'>Starts at</label>
          <select
            id='start_at'
            className='form-control'
            onChange={startAtChanged}
            defaultValue={state.startTime}
            disabled={readonly}
          >
            <TimeSelectOptions />
          </select>
        </div>
        <div className='form-group mb-3'>
          <label htmlFor='end_at'>Ends at</label>
          <select
            id='end_at'
            className='form-control'
            onChange={endAtChanged}
            defaultValue={state.endTime}
            disabled={readonly}
          >
            <TimeSelectOptions startingAt={state.startTime} />
          </select>
        </div>
      </div>
    </div>
  )
}

const DifferentTimeScheduler = ({ formObjectName, days, initialTimeSlots, removeDay, readonly, initialEndTime }) => {
  const [defaultTime, setDefaultTime] = useState(() => {
    return { startTime: null, endTime: null }
  })

  return (
    <div className='row'>
      <div className='col-12'>
        <label>DATE(S) SELECTED</label>
        {isEmpty(days) ? (
          <div className='row'>
            <EmptySchedule />
          </div>
        ) : (
          days.map((day, index) => (
            <DifferentTimeRow
              slot={slotFromDay(day, initialTimeSlots)}
              day={day}
              index={index}
              formObjectName={formObjectName}
              days={days}
              key={day.toString()}
              readonly={readonly}
              removeDay={() => {
                removeDay(day)
              }}
              defaultTime={defaultTime}
              setDefaultTime={setDefaultTime}
              initialEndTime={initialEndTime}
            />
          ))
        )}
      </div>
    </div>
  )
}

const DifferentTimeRow = ({
  day,
  slot,
  index,
  formObjectName,
  removeDay,
  readonly,
  defaultTime,
  setDefaultTime,
  initialEndTime
}) => {
  const [state, setState] = useState(() => {
    if (slot) {
      let startDay = startOfDay(parseDate(slot.start_at))
      let startTime = format(parseDate(slot.start_at), 'h:mm aaa')
      let endTime = format(parseDate(slot.end_at), 'h:mm aaa')
      return { day: startDay, startTime: startTime, endTime: endTime, id: slot.id }
    } else {
      return { day: day, startTime: '9:00 am', endTime: initialEndTime }
    }
  })

  const startAtChanged = event => {
    let eventValue = event.target.value
    setDefaultTime(defaultTime => ({ ...defaultTime, startTime: eventValue }))
    return setState(state => ({ ...state, startTime: eventValue }))
  }

  const endAtChanged = event => {
    let eventValue = event.target.value
    setDefaultTime(defaultTime => ({ ...defaultTime, endTime: eventValue }))
    return setState(state => ({ ...state, endTime: eventValue }))
  }

  return (
    <div className='row d-flex justify-content-between align-items-center justify-content-center'>
      <div className='form-group col-12 col-lg-4'>
        <div className='form-control'>{format(state.day, 'EEEE MMMM d, yyyy')}</div>
      </div>
      <div className='form-group col-5 col-lg-2'>
        <select
          className='form-control'
          onChange={startAtChanged}
          defaultValue={defaultTime.startTime || state.startTime}
          disabled={readonly}
        >
          <TimeSelectOptions />
        </select>
      </div>
      <div className='form-group col-1 d-flex justify-content-center'>
        <label htmlFor='to'>to</label>
      </div>
      <div className='form-group col-5 col-lg-2'>
        <select
          className='form-control'
          onChange={endAtChanged}
          defaultValue={defaultTime.endTime || state.endTime}
          disabled={readonly}
        >
          <TimeSelectOptions startingAt={state.startTime} />
        </select>
      </div>
      {state.id && (
        <input
          name={`${formObjectName}[time_slots_attributes][${index}][id]`}
          className='form-control hidden'
          type='hidden'
          value={state.id}
        />
      )}
      <div className='form-group col-12 col-sm-2 col-lg-2 d-flex'>
        {!readonly && (
          <a
            className='text-danger align-self-center cursor-pointer'
            id={`remove-${index}`}
            onClick={() => {
              removeDay(state.day)
            }}
          >
            Remove
          </a>
        )}
        <input
          name={`${formObjectName}[time_slots_attributes][${index}][start_at]`}
          className='form-control hidden'
          type='hidden'
          value={`${format(day, 'MMMM d, yyyy')} at ${state.startTime}`}
        />
        <input
          name={`${formObjectName}[time_slots_attributes][${index}][end_time]`}
          className='form-control hidden'
          type='hidden'
          value={state.endTime}
        />
      </div>
    </div>
  )
}

export default TimeSlotScheduler
